import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';
import md5 from 'md5';
import {
    FaCheck, FaPencilAlt, FaTrash, FaPlus, FaCalendarAlt, FaTimes, FaExclamationTriangle, FaCheckCircle, FaFlag, FaClock, FaEnvelope
} from 'react-icons/fa';
import Cookies from 'js-cookie';
import AppLayout from '../layout/AppLayout';
import regenerateToken from '../regenerateToken';
import {
    getMappingReportEmailHtml,
    sendMappingReportEmail,
    uploadMappingAndGetDownloadUrl,
    fetchParameterMappingData,
    fetchProfileMappingData,
} from '../../utils/mappingReportEmail';
import './onboardingStatus.css';
import { isAdmin as checkAdmin } from '../../utils/authUtils';
import { buildRequestConfig, getAdminSelectedClientId } from '../../utils/apiHelper';

// --- Constants ---
const PARAMETER_MAPPING_API = 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter';
const MAPPING_AUTO_CHECK_THRESHOLD = 90;

// --- Helper Functions ---
const getAuthHeaders = () => {
    const token = Cookies.get('idToken');
    return {
        headers: {
            'Authorization': token
        }
    };
};

const formatDate = (dateString, showTime = false) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    if (showTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
};

const getGravatarUrl = (email) => {
    if (!email) return '';
    const hash = md5(email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
};

// --- Components ---

const ProgressHeader = ({ steps, clientCreatedAt }) => {
    const totalSubsteps = steps.reduce((acc, step) => acc + (step.substeps?.length || 0), 0);
    const completedSubsteps = steps.reduce((acc, step) => {
        return acc + (step.substeps?.filter(s => s.is_substep_completed)?.length || 0);
    }, 0);

    const percentage = totalSubsteps === 0 ? 0 : Math.round((completedSubsteps / totalSubsteps) * 100);

    // Find active step
    const activeStep = steps.find(s => s.status !== 'completed');
    const isAllComplete = steps.length > 0 && !activeStep;

    // Circular Progress Props
    const radius = 58;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    return (
        <div className="os-stats-wrapper">
            {/* Left: Circular Progress */}
            <div className="os-circular-progress">
                <svg className="os-circular-svg" viewBox="0 0 140 140">
                    <defs>
                        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#2563eb" />
                            <stop offset="100%" stopColor="#60a5fa" />
                        </linearGradient>
                    </defs>
                    <circle
                        className="os-circular-bg"
                        cx="70"
                        cy="70"
                        r={radius}
                    />
                    <circle
                        className="os-circular-fg"
                        cx="70"
                        cy="70"
                        r={radius}
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                    />
                </svg>
                <div className="os-circular-text">
                    <span className="os-circular-value">{percentage}%</span>
                    <span className="os-circular-label">Done</span>
                </div>
            </div>

            {/* Right: Detailed Stats */}
            <div className="os-stats-content">
                <div className="os-stat-box">
                    <span className="os-stat-label">
                        <FaCheckCircle size={14} /> Completed Tasks
                    </span>
                    <span className="os-stat-value">
                        {completedSubsteps} <span style={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>/ {totalSubsteps}</span>
                    </span>
                    <span className="os-stat-desc">Substeps finished across all stages</span>
                </div>

                <div className="os-stat-box">
                    <span className="os-stat-label">
                        <FaFlag size={14} /> Current Stage
                    </span>
                    <span className="os-stat-value" style={{ fontSize: '1.35rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '250px', display: 'block' }} title={activeStep?.step_name}>
                        {isAllComplete ? 'All Completed' : (activeStep?.step_name || 'Not Started')}
                    </span>
                    <span className="os-stat-desc">
                        {isAllComplete
                            ? 'Onboarding successfully finished'
                            : 'Currently in progress'}
                    </span>
                </div>
            </div>
        </div>
    );
};

const StatusCard = ({ step, onEdit, isAdmin, onSendMappingReport, sendReportLoading }) => {
    const isActive = step.status === 'inProgress';

    return (
        <div className={`os-card ${isActive ? 'active' : ''}`}>
            <div className="os-card-header">
                <div>
                    <h4 className="os-card-title">{step.step_name}</h4>
                    {isActive && <div className="os-card-subtitle">Currently Active</div>}
                </div>
                <div className="os-card-actions">
                    <button className="os-icon-btn" onClick={() => onEdit(step)}>
                        <FaPencilAlt size={14} />
                    </button>
                </div>
            </div>

            <div className="os-substep-list">
                {step.substeps?.map((sub, idx) => {
                    const isCompleted = sub.is_substep_completed;
                    const isBiomarkerMapping = (sub.substep_name || '').toLowerCase().includes('biomarker');
                    const showEmailOnRow = isBiomarkerMapping && onSendMappingReport;

                    return (
                        <div
                            key={idx}
                            className={`os-substep-item ${isCompleted ? 'success' : 'warning'}`}
                        >
                            <div className="os-substep-left">
                                <div className={`os-substep-check ${isCompleted ? 'success' : 'warning'}`}>
                                    {isCompleted ? <FaCheck size={10} /> : <FaExclamationTriangle size={10} />}
                                </div>
                                <span className={`os-substep-label ${isCompleted ? 'success' : 'warning'}`}>
                                    {sub.substep_name}
                                </span>
                            </div>
                            {showEmailOnRow && (
                                <button
                                    className="os-icon-btn os-send-report-btn "
                                    onClick={() => onSendMappingReport(step)}
                                    disabled={sendReportLoading}
                                    title="Send Mapping Report to Client"
                                >
                                    {sendReportLoading ? (
                                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14 }} />
                                    ) : (
                                        <FaEnvelope size={14} />
                                    )}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const UpdateStepStatusPopup = ({ isOpen, onClose, step, onSave, isCreating }) => {
    const [formData, setFormData] = useState({
        step_name: '',
        deadline_date: '',
        status: 'notStarted',
        dependencyArray: [],
        substeps: []
    });
    const [newEmail, setNewEmail] = useState('');
    const [substepName, setSubstepName] = useState('');
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (step) {
            setFormData({
                ...step,
                deadline_date: step.deadline_date ? step.deadline_date.split('T')[0] : ''
            });
        } else {
            setFormData({
                step_name: '',
                deadline_date: '',
                status: 'notStarted',
                dependencyArray: [],
                substeps: []
            });
        }
        setFormErrors({}); // Clear errors when modal opens/changes
    }, [step, isOpen]);

    const handleSave = () => {
        // Validate required fields
        const errors = {};

        if (!formData.step_name || formData.step_name.trim() === '') {
            errors.step_name = 'Step name is required';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return; // Don't call onSave if validation fails
        }

        setFormErrors({});
        onSave(formData);
    };

    const addSubstep = () => {
        if (!substepName) return;
        setFormData({
            ...formData,
            substeps: [...formData.substeps, { substep_name: substepName, is_substep_completed: false, completed_by: null }]
        });
        setSubstepName('');
    };

    const toggleSubstep = (idx) => {
        const updated = [...formData.substeps];
        updated[idx].is_substep_completed = !updated[idx].is_substep_completed;
        if (updated[idx].is_substep_completed) {
            updated[idx].completed_at = new Date();
        } else {
            updated[idx].completed_at = null;
        }
        setFormData({ ...formData, substeps: updated });
    };

    const removeSubstep = (idx) => {
        const updated = [...formData.substeps];
        updated.splice(idx, 1);
        setFormData({ ...formData, substeps: updated });
    };

    const addEmail = () => {
        if (!newEmail) return;
        setFormData({
            ...formData,
            dependencyArray: [...(formData.dependencyArray || []), newEmail]
        });
        setNewEmail('');
    };

    const removeEmail = (email) => {
        setFormData({
            ...formData,
            dependencyArray: formData.dependencyArray.filter(e => e !== email)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="os-modal-overlay">
            <div className="os-modal">
                <div className="os-modal-header">
                    <h3 className="os-modal-title">{isCreating ? 'Create New Step' : 'Update Step'}</h3>
                    <button onClick={onClose} className="os-icon-btn"><FaTimes /></button>
                </div>
                <div className="os-modal-body">
                    <div className="os-form-group">
                        <label className="os-form-label">Step Name *</label>
                        <input
                            className={`os-form-input ${formErrors.step_name ? 'error' : ''}`}
                            value={formData.step_name}
                            onChange={e => {
                                setFormData({ ...formData, step_name: e.target.value });
                                if (formErrors.step_name) {
                                    setFormErrors({ ...formErrors, step_name: '' });
                                }
                            }}
                        />
                        {formErrors.step_name && (
                            <span className="os-form-error">{formErrors.step_name}</span>
                        )}
                    </div>
                    <div className="os-form-group">
                        <label className="os-form-label">Deadline</label>
                        <input
                            type="date"
                            className="os-form-input"
                            value={formData.deadline_date}
                            onChange={e => setFormData({ ...formData, deadline_date: e.target.value })}
                        />
                    </div>

                    {!isCreating && (
                        <div className="os-form-group">
                            <label className="os-form-label">Status</label>
                            <select
                                className="os-form-select"
                                value={formData.status}
                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                            >
                                <option value="notStarted">Not Started</option>
                                <option value="inProgress">In Progress</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                    )}



                    <div className="os-form-group">
                        <label className="os-form-label">Substeps Checklist</label>
                        <div className="os-modal-checklist">
                            {formData.substeps?.map((sub, idx) => (
                                <div key={idx} className="os-checklist-item">
                                    <input
                                        type="checkbox"
                                        className="os-checklist-checkbox"
                                        checked={sub.is_substep_completed}
                                        onChange={() => toggleSubstep(idx)}
                                    />
                                    <span className="os-checklist-text" style={{ textDecoration: sub.is_substep_completed ? 'line-through' : 'none', opacity: sub.is_substep_completed ? 0.6 : 1 }}>
                                        {sub.substep_name}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="os-add-row">
                            <input
                                className="os-form-input"
                                placeholder="Add new item..."
                                value={substepName}
                                onChange={e => setSubstepName(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addSubstep()}
                            />
                            <button className="os-btn-primary" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }} onClick={addSubstep}>Add</button>
                        </div>
                    </div>

                </div>
                <div className="os-modal-footer">
                    <button className="os-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="os-btn-primary" onClick={handleSave}>Save Changes</button>
                </div>
            </div>
        </div>
    );
};


const OnboardingStatus = () => {
    const [steps, setSteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedStep, setSelectedStep] = useState(null);
    const [userData, setUserData] = useState(null);
    const [sampleData, setSampleData] = useState(null); // Store parsed sample JSON
    const [verificationComplete, setVerificationComplete] = useState(false); // Track if auto-verification ran
    const [error, setError] = useState(null); // Track API errors
    const [failedUpdates, setFailedUpdates] = useState([]); // Track failed API updates
    const [retryLoading, setRetryLoading] = useState({}); // Track loading state per retry
    const [sendReportLoadingByStep, setSendReportLoadingByStep] = useState({});
    const alert = useAlert();
    const alertRef = useRef(alert);

    // Ref guard: prevents runMappingAutoCheck from firing more than once per session.
    const mappingAutoCheckRanRef = useRef(false);

    // Keep alertRef in sync with alert from hook
    useEffect(() => {
        alertRef.current = alert;
    }, [alert]);

    // Base URL
    const BASE_URL = 'https://api.niroggyan.com/onboarding/status';

    // Helper: get the effective clientId for API calls
    // ── For admin: uses the client selected in the admin panel (from sessionStorage)
    // ── For client: uses their own _id or clientId from stored credentials
    const getEffectiveClientId = () => {
        if (checkAdmin()) {
            // Admin must have a client selected to view their onboarding status
            const selectedClientId = getAdminSelectedClientId();
            return selectedClientId || null;
        }
        // Regular client - use their own ID
        return userData?._id || userData?.clientId || null;
    };

    useEffect(() => {
        try {
            const creds = localStorage.getItem('credentials');
            if (creds) {
                const parsedData = JSON.parse(creds);
                if (parsedData) {
                    setUserData(parsedData);
                }
            }
        } catch (error) {
            console.error('Failed to parse stored credentials:', error);
            localStorage.removeItem('credentials'); // Clean up corrupted data
            if (alertRef.current) alertRef.current.error('Session data corrupted. Please log in again.');
        }
    }, []); // Removed alert dependency, using stable ref

    const fetchSteps = async () => {
        if (!userData) return;

        // For admin: require a selected client
        const effectiveClientId = getEffectiveClientId();
        if (checkAdmin() && !effectiveClientId) {
            setError({
                type: 'info',
                message: 'Please select a client from the top bar to view their onboarding status.'
            });
            setLoading(false);
            return;
        }

        if (!effectiveClientId) {
            setError({ type: 'error', message: 'Client ID not found. Please log in again.' });
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(
                `${BASE_URL}?clientId=${effectiveClientId}`,
                getAuthHeaders()
            );
            // API returns { data: [...] }
            const fetchedSteps = res.data?.data || [];
            setSteps(fetchedSteps);

            // If no steps found, set a friendly error state
            if (fetchedSteps.length === 0) {
                setError({ type: 'empty', message: 'No onboarding steps configured yet for this client.' });
            }

            setLoading(false);
        } catch (error) {
            console.error("Fetch steps error", error);
            let errorMessage;
            if (error?.response?.status === 404) {
                errorMessage = checkAdmin()
                    ? 'No onboarding record found for the selected client.'
                    : 'No onboarding record found for your account.';
            } else if (error?.response?.status === 403) {
                errorMessage = 'You do not have permission to view onboarding status.';
            } else {
                errorMessage = 'Failed to load onboarding status. Please try again.';
            }
            setError({ type: 'error', message: errorMessage });
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userData) {
            fetchSteps();
        }
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    // For admin: re-fetch when the selected client changes in the top bar
    useEffect(() => {
        if (!checkAdmin()) return;

        const handleStorageChange = (e) => {
            if (e.key === 'adminSelectedClient') {
                // Reset state and re-fetch for the newly selected client
                setSteps([]);
                setError(null);
                setLoading(true);
                // Use a small delay to allow sessionStorage to fully update
                setTimeout(() => fetchSteps(), 100);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch Sample Data for UI Implementation (Only Once)
    useEffect(() => {
        if (!userData) return;

        // Check if verification already completed for this client
        const verificationKey = `onboarding_verified_${userData.clientId || userData._id}`;
        const alreadyVerified = localStorage.getItem(verificationKey);

        if (alreadyVerified === 'true') {
            console.log('✓ Auto-verification already completed for this client. Skipping API calls.');
            setVerificationComplete(true);
            return;
        }

        const fetchSampleData = async () => {
            try {
                const org = userData.org;
                const center = userData.center;
                const reportType = userData.reportSubscription?.reportType || 'advanced';
                const count = 1;

                console.log('🔄 Running one-time auto-verification...');

                // 1. Get List of URLs
                const url = `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/clientdashboard/sample?org=${org}&center=${center}&report_type=${reportType}&count=${count}`;
                const response = await axios.get(url);
                console.log("Sample API Response:", response.data);

                if (response.data && response.data.length > 0 && response.data[0].url) {
                    // 2. Fetch the actual JSON content from the first URL
                    const s3Url = response.data[0].url;
                    const jsonApiUrl = `https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/inputjson/s3json?s3url=${s3Url}`;

                    const jsonResponse = await axios.get(jsonApiUrl);
                    console.log("Validation JSON Content:", jsonResponse.data);
                    setSampleData(jsonResponse.data);
                } else {
                    console.log('⚠ No sample data available for verification.');
                    // Mark as complete even if no data, to avoid repeated calls
                    localStorage.setItem(verificationKey, 'true');
                    setVerificationComplete(true);
                }

            } catch (error) {
                console.error("Error fetching sample data:", error);
                // Don't mark as complete on error, allow retry on next page load
            }
        };
        fetchSampleData();
    }, [userData]);

    // Auto-Verification Logic (Runs only once when sampleData is available)
    useEffect(() => {
        if (!sampleData || !steps || steps.length === 0 || verificationComplete) return;

        // Helper: Check if a value exists and is not empty
        const hasValue = (val) => val && typeof val === 'string' && val.trim().length > 0;

        const verifyStep = async (step) => {
            let isModified = false;
            // Deep clone to safely modify
            const updatedSubsteps = step.substeps.map(sub => ({ ...sub }));

            updatedSubsteps.forEach(sub => {
                if (sub.is_substep_completed) return; // Already done

                const name = sub.substep_name.toLowerCase();
                let shouldComplete = false;

                // Check Header (must have actual base64 content)
                if (name.includes("header") && (hasValue(sampleData.headerBase64) || hasValue(sampleData.header_base64))) {
                    shouldComplete = true;
                }
                // Check Footer (must have actual base64 content)
                if (name.includes("footer") && (hasValue(sampleData.footerBase64) || hasValue(sampleData.footer_base64))) {
                    shouldComplete = true;
                }
                // Check Signature (must have actual signature data)
                if ((name.includes("signature") || name.includes("doctor")) &&
                    (hasValue(sampleData.ApprovedDoctorSign) || hasValue(sampleData.DoctorSignature) || hasValue(sampleData.auto_signature))) {
                    shouldComplete = true;
                }
                // Check Brand Logo (must have actual logo data)
                if ((name.includes("logo") || name.includes("accreditation")) &&
                    (hasValue(sampleData.brandLogoBase64) || hasValue(sampleData.BrandLogo))) {
                    shouldComplete = true;
                }
                // Check JSON (if we received valid JSON data, it's present)
                if (name.includes("json") && sampleData && Object.keys(sampleData).length > 0) {
                    shouldComplete = true;
                }

                if (shouldComplete) {
                    sub.is_substep_completed = true;
                    sub.completed_at = new Date();
                    isModified = true;
                    console.log(`✓ Auto-verified: ${sub.substep_name}`);
                }
            });

            if (isModified) {
                // Trigger Update
                // IMPORTANT: Do NOT include _id in the payload - it causes "\"_id\" is not allowed" error
                const payload = {
                    step_name: step.step_name,
                    deadline_date: step.deadline_date,
                    status: step.status,
                    completed_by: step.completed_by,
                    dependencyArray: step.dependencyArray || [],
                    substeps: updatedSubsteps,
                    created_at: step.created_at,
                    clientId: step.clientId,
                    completed_at: step.status === 'completed' ? step.completed_at : null
                };

                // Optimistic UI Update: Update local state immediately
                setSteps(prevSteps => prevSteps.map(s => s._id === step._id ? { ...s, substeps: updatedSubsteps } : s));

                // Attempt Backend Save
                try {
                    await axios.post(`${BASE_URL}/updatestep?id=${step._id}`, payload, getAuthHeaders());
                    console.log(`✓ Auto-update saved for step: ${step.step_name}`);

                    // Remove from failed updates if it was previously failed
                    setFailedUpdates(prev => prev.filter(f => f.stepId !== step._id));
                } catch (err) {
                    console.error("⚠ Auto-update failed:", err?.response?.data || err.message);

                    // Track failed update
                    const failedUpdate = {
                        stepId: step._id,
                        stepName: step.step_name,
                        payload,
                        timestamp: new Date(),
                        error: err?.response?.data?.message || err.message
                    };

                    setFailedUpdates(prev => {
                        // Avoid duplicates
                        const filtered = prev.filter(f => f.stepId !== step._id);
                        return [...filtered, failedUpdate];
                    });

                    // Show user-visible warning
                    if (alertRef.current) alertRef.current.error(`Failed to save changes for "${step.step_name}". Changes are shown but not saved.`);
                }
            }
        };

        // Process all steps
        const processSteps = async () => {
            for (const step of steps) {
                if (step.substeps && step.substeps.length > 0) {
                    await verifyStep(step);
                }
            }

            // Mark verification as complete and store in localStorage
            const verificationKey = `onboarding_verified_${userData?.clientId || userData?._id}`;
            localStorage.setItem(verificationKey, 'true');
            setVerificationComplete(true);
            console.log('✅ Auto-verification completed. Will not run again for this client.');
        };

        processSteps();

    }, [sampleData, steps, verificationComplete, userData]); // Removed alert dependency, using stable ref

    // Mapping Auto-Check: When Biomarker + Profile Mapping both false, call parameter GET and auto-complete if count >= threshold
    useEffect(() => {
        if (!userData || !steps || steps.length === 0) return;

        const findMappingStepAndSubsteps = () => {
            const mappingStep = steps.find(s =>
                s.step_name && s.step_name.toLowerCase().includes('mapping')
            );
            if (!mappingStep?.substeps?.length) return { step: null, biomarkerIdx: -1, profileIdx: -1 };

            let biomarkerIdx = -1;
            let profileIdx = -1;
            mappingStep.substeps.forEach((sub, idx) => {
                const name = (sub.substep_name || '').toLowerCase();
                if (name.includes('biomarker')) biomarkerIdx = idx;
                if (name.includes('profile')) profileIdx = idx;
            });

            if (biomarkerIdx < 0 || profileIdx < 0) return { step: null, biomarkerIdx: -1, profileIdx: -1 };
            return { step: mappingStep, biomarkerIdx, profileIdx };
        };

        const { step: mappingStep, biomarkerIdx, profileIdx } = findMappingStepAndSubsteps();
        if (!mappingStep || biomarkerIdx < 0 || profileIdx < 0) return;

        const biomarkerSub = mappingStep.substeps[biomarkerIdx];
        const profileSub = mappingStep.substeps[profileIdx];
        if (biomarkerSub.is_substep_completed || profileSub.is_substep_completed) return;

        // Guard: run at most once per mount/session to prevent re-entry
        // when setSteps() above triggers a re-render with updated steps.
        if (mappingAutoCheckRanRef.current) return;
        mappingAutoCheckRanRef.current = true;

        const runMappingAutoCheck = async () => {
            let response;
            // ── ADMIN PATH: use admin token + inject clientId if a client is selected
            if (checkAdmin()) {
                response = await axios(buildRequestConfig({
                    method: 'GET',
                    url: PARAMETER_MAPPING_API,
                }));
                if (response.status === 401) {
                    await regenerateToken();
                    response = await axios(buildRequestConfig({
                        method: 'GET',
                        url: PARAMETER_MAPPING_API,
                    }));
                }
            } else {
                // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
                const idToken = Cookies.get('idToken');
                const config = {
                    headers: { authorization: idToken },
                    method: 'GET',
                    url: PARAMETER_MAPPING_API,
                    validateStatus: (s) => s >= 200 && s <= 401,
                };
                response = await axios(config);
                if (response.status === 401) {
                    await regenerateToken();
                    response = await axios({ ...config, headers: { authorization: Cookies.get('idToken') } });
                }
            }

            if (!response?.data?.isSuccess) return;

            const idMapping = response.data?.data?.idMapping || {};
            const count = Object.keys(idMapping).length;
            if (count < MAPPING_AUTO_CHECK_THRESHOLD) return;

            const updatedSubsteps = mappingStep.substeps.map((sub, idx) => {
                if (idx === biomarkerIdx || idx === profileIdx) {
                    return {
                        ...sub,
                        is_substep_completed: true,
                        completed_at: new Date(),
                    };
                }
                return { ...sub };
            });

            const payload = {
                step_name: mappingStep.step_name,
                deadline_date: mappingStep.deadline_date,
                status: mappingStep.status,
                completed_by: mappingStep.completed_by,
                dependencyArray: mappingStep.dependencyArray || [],
                substeps: updatedSubsteps,
                created_at: mappingStep.created_at,
                clientId: mappingStep.clientId,
                completed_at: mappingStep.status === 'completed' ? mappingStep.completed_at : null,
            };

            setSteps(prev => prev.map(s =>
                s._id === mappingStep._id ? { ...s, substeps: updatedSubsteps } : s
            ));

            try {
                await axios.post(`${BASE_URL}/updatestep?id=${mappingStep._id}`, payload, getAuthHeaders());
                console.log('✓ Mapping auto-check: Biomarker & Profile Mapping completed (count >= 90)');
                setFailedUpdates(prev => prev.filter(f => f.stepId !== mappingStep._id));
            } catch (err) {
                console.error('⚠ Mapping auto-check save failed:', err?.response?.data || err.message);
                setFailedUpdates(prev => {
                    const filtered = prev.filter(f => f.stepId !== mappingStep._id);
                    return [...filtered, {
                        stepId: mappingStep._id,
                        stepName: mappingStep.step_name,
                        payload,
                        timestamp: new Date(),
                        error: err?.response?.data?.message || err.message,
                    }];
                });
                if (alertRef.current) alertRef.current.error(`Failed to save mapping auto-check for "${mappingStep.step_name}".`);
            }
        };

        runMappingAutoCheck();
    }, [userData, steps]);

    const handleCreate = async (formData) => {
        try {
            const effectiveClientId = getEffectiveClientId();
            // Payload structure match for Create
            const payload = {
                step_name: formData.step_name,
                deadline_date: formData.deadline_date,
                status: 'notStarted',
                completed_by: null,
                dependencyArray: formData.dependencyArray,
                substeps: formData.substeps,
                created_at: new Date(),
                clientId: effectiveClientId
            };

            await axios.post(`${BASE_URL}/createstep`, payload, getAuthHeaders());
            alert.success("Step created successfully!");
            setShowModal(false);
            fetchSteps();
        } catch (error) {
            const msg = error?.response?.data?.message || "Failed to create step.";
            alert.error(String(msg).includes("authorized") ? "Permission Denied: Admin access required." : msg);
        }
    };

    const handleUpdate = async (formData) => {
        try {
            const effectiveClientId = getEffectiveClientId();
            // Payload structure match for Update
            // IMPORTANT: Do NOT include _id in the payload - it causes "\"_id\" is not allowed" error
            // The _id is only used in the query parameter
            const payload = {
                step_name: formData.step_name,
                deadline_date: formData.deadline_date,
                status: formData.status,
                completed_by: formData.completed_by,
                dependencyArray: formData.dependencyArray || [],
                substeps: formData.substeps || [],
                created_at: formData.created_at, // Preserve original
                clientId: effectiveClientId,
                completed_at: formData.status === 'completed' && !formData.completed_at ? new Date() : formData.completed_at
            };

            // Docs: /updatestep?id={stepId}
            await axios.post(`${BASE_URL}/updatestep?id=${formData._id}`, payload, getAuthHeaders());
            alert.success("Step updated successfully!");
            setShowModal(false);
            fetchSteps();
        } catch (error) {
            const msg = error?.response?.data?.error || error?.response?.data?.message || "Failed to update step.";
            alert.error(String(msg).includes("authorized") ? "Permission Denied: Admin access required." : msg);
        }
    };

    const handleDelete = async (stepId) => {
        if (!window.confirm("Are you sure you want to delete this step?")) return;
        try {
            await axios.delete(`${BASE_URL}/deletestep?id=${stepId}`, getAuthHeaders());
            alert.success("Step deleted.");
            fetchSteps();
        } catch (error) {
            const msg = error?.response?.data?.message || "Failed to delete step.";
            alert.error(String(msg).includes("authorized") ? "Permission Denied: Admin access required." : msg);
        }
    };

    const retryFailedUpdate = async (failedUpdate) => {
        const stepId = failedUpdate.stepId;

        // Set loading state for this specific retry
        setRetryLoading(prev => ({ ...prev, [stepId]: true }));

        try {
            await axios.post(`${BASE_URL}/updatestep?id=${stepId}`, failedUpdate.payload, getAuthHeaders());

            // Remove from failed updates on success
            setFailedUpdates(prev => prev.filter(f => f.stepId !== stepId));
            alert.success(`Successfully saved changes for "${failedUpdate.stepName}"`);

            // Refresh to get latest state
            fetchSteps();
        } catch (err) {
            console.error("Retry failed:", err);
            alert.error(`Retry failed for "${failedUpdate.stepName}". Please try again later.`);
        } finally {
            // Clear loading state
            setRetryLoading(prev => ({ ...prev, [stepId]: false }));
        }
    };

    const clearFailedUpdate = (stepId) => {
        setFailedUpdates(prev => prev.filter(f => f.stepId !== stepId));
    };

    const closeAndReset = () => {
        setShowModal(false);
        setSelectedStep(null);
    };

    const isMappingStepWithBothComplete = (step) => {
        if (!step?.step_name?.toLowerCase().includes('mapping') || !step?.substeps?.length) return false;
        let biomarkerComplete = false;
        let profileComplete = false;
        step.substeps.forEach((sub) => {
            const name = (sub.substep_name || '').toLowerCase();
            if (name.includes('biomarker')) biomarkerComplete = !!sub.is_substep_completed;
            if (name.includes('profile')) profileComplete = !!sub.is_substep_completed;
        });
        return biomarkerComplete && profileComplete;
    };

    const traceMode = window.location.search.includes('trace=1');

    const handleSendMappingReport = async (step) => {
        // ── Guard: need client email ───────────────────────────────────────
        const mailIds = userData?.notifications?.mailIds;
        if (!mailIds || !Array.isArray(mailIds) || mailIds.length === 0) {
            alert.error('Client email not configured. Please check notifications.mailIds.');
            return;
        }

        const org = userData?.org;
        const center = userData?.center;
        if (!org || !center) {
            alert.error('Organization and center are required for the mapping report.');
            return;
        }

        setSendReportLoadingByStep(prev => ({ ...prev, [step._id]: true }));
        try {
            // ── Step 1: Fetch BOTH mapping APIs in parallel ────────────────
            //   - Parameter mapping: { parameterId: ngStandardName }
            //   - Profile mapping:   { ngStandardName: profileName }
            const [idMapping, profileMapping] = await Promise.all([
                fetchParameterMappingData(),
                fetchProfileMappingData(),
            ]);

            const paramCount = Object.keys(idMapping).length;
            const profileCount = new Set(Object.values(profileMapping).filter(Boolean)).size;
            const clientName = userData?.customerName || userData?.displayName || userData?.org || 'Client';
            const dateStr = new Date().toLocaleDateString('en-IN', {
                year: 'numeric', month: 'long', day: 'numeric',
            });

            if (traceMode) {
                alert.info(`✓ Step 1: Fetched ${paramCount} parameters across ${profileCount} profiles`);
            }

            // ── Step 2: Build combined styled XLSX, upload to S3, get link ────
            //   uploadMappingAndGetDownloadUrl:
            //     a) GET presigned PUT URL
            //     b) Build 2-sheet styled XLSX (exceljs)
            //     c) PUT upload to S3
            //     d) GET presigned download URL
            let downloadUrl;
            try {
                downloadUrl = await uploadMappingAndGetDownloadUrl(
                    org, center, idMapping, profileMapping
                );
                if (traceMode) alert.info('✓ Step 2: Combined XLSX uploaded to S3 — link ready');
            } catch (uploadErr) {
                console.error('[MappingReport] Upload/presign failed:', uploadErr);
                if (traceMode) {
                    const d = uploadErr?.response?.status
                        ? `HTTP ${uploadErr.response.status}`
                        : uploadErr?.message || 'Unknown';
                    alert.error(`✗ Step 2: Failed — ${d}`);
                }
                alert.error('Could not prepare the mapping report for download. Please try again.');
                return;
            }

            // ── Step 3: Build email ───────────────────────────────────────────
            const emailBody = getMappingReportEmailHtml(
                paramCount, profileCount, clientName, dateStr, downloadUrl
            );

            // ── Step 4: Send email ───────────────────────────────────────────
            const result = await sendMappingReportEmail({
                toEmail: mailIds,
                emailBody,
                subject: `Parameter & Profile Mapping Report — ${paramCount} parameters across ${profileCount} profiles`,
            });

            if (result.success) {
                if (traceMode) alert.info('✓ Step 3: Email sent to ' + (mailIds?.join(', ') || 'client'));
                alert.success(`Mapping report sent to ${mailIds.join(', ')} successfully.`);
            } else {
                const errMsg = result.error?.response?.data?.message || result.error?.message || 'Unknown error.';
                if (traceMode) alert.error('✗ Step 3: Email failed — ' + errMsg);
                alert.error(`Email delivery failed: ${errMsg}`);
            }
        } catch (err) {
            console.error('[MappingReport] Unexpected error:', err);
            const errMsg = err?.message || 'Unexpected error.';
            if (traceMode) alert.error('✗ Unexpected: ' + errMsg);
            alert.error(`Failed to send mapping report: ${errMsg}`);
        } finally {
            setSendReportLoadingByStep(prev => ({ ...prev, [step._id]: false }));
        }
    };

    // Derive isAdmin from user credentials instead of hardcoding
    const isAdmin = userData?.isAdmin === true || userData?.role === 'admin' || userData?.permissions?.includes('onboarding.manage') || false;

    return (
        <AppLayout>
            <div className="os-container">
                {!loading && !error && (
                    <div className="os-header">
                        <div className="os-title-area">
                            <h1 className="os-title">Onboarding Status</h1>
                            {isAdmin && (
                                <button className="os-btn-primary" onClick={() => setShowModal(true)}>
                                    <FaPlus /> Create Step
                                </button>
                            )}
                        </div>

                        <ProgressHeader steps={steps} clientCreatedAt={userData?.created_at} />
                    </div>
                )}

                {/* Failed Updates Banner */}
                {failedUpdates.length > 0 && (
                    <div className="os-failed-updates-banner">
                        <div className="os-failed-updates-header">
                            <FaExclamationTriangle />
                            <span>Some changes failed to save ({failedUpdates.length})</span>
                        </div>
                        <div className="os-failed-updates-list">
                            {failedUpdates.map((failed) => (
                                <div key={failed.stepId} className="os-failed-update-item">
                                    <div className="os-failed-update-info">
                                        <strong>{failed.stepName}</strong>
                                        <span className="os-failed-update-error">{failed.error}</span>
                                    </div>
                                    <div className="os-failed-update-actions">
                                        <button
                                            className="os-btn-retry"
                                            onClick={() => retryFailedUpdate(failed)}
                                            disabled={retryLoading[failed.stepId]}
                                        >
                                            {retryLoading[failed.stepId] ? 'Retrying...' : 'Retry'}
                                        </button>
                                        <button
                                            className="os-btn-dismiss"
                                            onClick={() => clearFailedUpdate(failed.stepId)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="os-loading-container">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                        <p style={{ marginTop: '1rem', color: '#64748b' }}>Loading onboarding status...</p>
                    </div>
                ) : error ? (
                    <div className="os-empty-state">
                        <div className="os-empty-icon">
                            {error.type === 'empty' ? '📋' : error.type === 'info' ? '📌' : '⚠️'}
                        </div>
                        <h3 className="os-empty-title">
                            {error.type === 'empty'
                                ? 'No Onboarding Steps Yet'
                                : error.type === 'info'
                                    ? 'Select a Client'
                                    : 'Unable to Load Onboarding'}
                        </h3>
                        <p className="os-empty-message">{error.message}</p>
                        {error.type !== 'info' && (
                            <button className="os-btn-primary" onClick={fetchSteps} style={{ marginTop: '1rem' }}>
                                <FaPlus /> Try Again
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="os-step-grid">
                        {steps.filter(s => s.step_name !== 'Client Side Testing').map(step => (
                            <StatusCard
                                key={step._id}
                                step={step}
                                isAdmin={isAdmin}
                                onSendMappingReport={
                                    isMappingStepWithBothComplete(step) ? handleSendMappingReport : undefined
                                }
                                sendReportLoading={sendReportLoadingByStep[step._id] === true}
                                onEdit={(s) => {
                                    setSelectedStep(s);
                                    setShowModal(true);
                                }}
                            />
                        ))}
                    </div>
                )}


                <UpdateStepStatusPopup
                    isOpen={showModal}
                    onClose={closeAndReset}
                    step={selectedStep}
                    isCreating={!selectedStep}
                    onSave={selectedStep ? handleUpdate : handleCreate}
                />
            </div>
        </AppLayout>
    );
};

export default OnboardingStatus;
